import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Share2, MessageCircle, Facebook, Link as LinkIcon, X, Instagram, Smartphone, Check } from 'lucide-react';
import { api } from '../services/api';
import { gerarTextoCompartilhamento } from '../utils/shareContent';
import { buildWhatsAppShareUrl } from '../utils/whatsappLink';
import { copyToClipboard } from '../utils/copyToClipboard';
import CompartilharFeedback from './CompartilharFeedback';

// Lucide doesn't ship an X (Twitter) icon yet — render the official glyph as
// a tiny inline SVG so the button keeps the platform's current branding.
function XIcon({ className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/**
 * ShareButton — botão universal de compartilhamento.
 *
 * Props:
 *   - business: entidade compartilhada (obrigatório)
 *   - tipo:     'negocio' | 'promocao' | 'evento' | 'noticia' (default: 'negocio')
 *   - municipio: { nome?, slug? } (opcional — derivado do business quando ausente)
 *   - size:     'sm' | 'md' | 'lg'
 *   - className: classes extra do trigger
 */
export default function ShareButton({ business, tipo = 'negocio', municipio, size = 'md', className = '' }) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedFor, setCopiedFor] = useState(''); // '', 'instagram', 'status', 'link'
  const [feedbackVisivel, setFeedbackVisivel] = useState(false);
  const [feedbackMetodo, setFeedbackMetodo] = useState(null);

  // Texto publicitário centralizado — varia por tipo (negocio/promocao/evento/noticia)
  const { titulo: shareTitle, texto: shareText, url: businessUrl } =
    gerarTextoCompartilhamento(tipo, business, municipio);

  const showFeedback = (metodo) => {
    setFeedbackMetodo(metodo);
    setFeedbackVisivel(true);
  };

  const trackShare = async (platform) => {
    try {
      const negocioId = tipo === 'negocio'
        ? (business?.id || business?.negocio_id)
        : (business?.negocio_id || business?.business_id || business?.id);
      if (!negocioId) return;
      await api.post(`/analytics/share/${negocioId}?platform=${platform}`);
    } catch (error) {
      console.error('Erro ao rastrear compartilhamento:', error);
    }
  };

  // Native Web Share API — exposes the OS share sheet which includes
  // Stories/Status options natively on mobile. Best UX when available.
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: shareTitle, text: shareText, url: businessUrl });
      trackShare('native');
      setShowModal(false);
      showFeedback('native');
    } catch {
      /* user cancelled or share failed — silent */
    }
  };

  const handleWhatsAppShare = () => {
    trackShare('whatsapp');
    const url = buildWhatsAppShareUrl(`${shareText}\n${businessUrl}`);
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowModal(false);
    showFeedback('whatsapp');
  };

  // WhatsApp Status: there is no public web URL to post directly to Status,
  // so we copy the link and instruct the user to paste it from the app.
  const handleWhatsAppStatusShare = async () => {
    trackShare('whatsapp_status');
    const ok = await copyToClipboard(`${shareText}\n${businessUrl}`);
    if (ok) {
      setCopiedFor('status');
      setTimeout(() => setCopiedFor(''), 2500);
      showFeedback('clipboard');
    } else {
      const text = encodeURIComponent(`${shareText}\n${businessUrl}`);
      window.open(`whatsapp://send?text=${text}`, '_blank');
      showFeedback('whatsapp');
    }
  };

  const handleFacebookShare = () => {
    trackShare('facebook');
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(businessUrl)}`,
      '_blank',
      'width=600,height=400'
    );
    setShowModal(false);
    showFeedback('native');
  };

  const handleXShare = () => {
    trackShare('twitter');
    const text = encodeURIComponent(shareText);
    const url = encodeURIComponent(businessUrl);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      '_blank',
      'width=600,height=400'
    );
    setShowModal(false);
    showFeedback('native');
  };

  // Instagram doesn't support direct web share, so we copy the link and tell
  // the user to paste in their bio / Stories. On mobile we also try the
  // deep link to open the app.
  const handleInstagramShare = async () => {
    trackShare('instagram');
    const ok = await copyToClipboard(businessUrl);
    if (ok) {
      setCopiedFor('instagram');
      setTimeout(() => setCopiedFor(''), 2500);
      showFeedback('clipboard');
    }
  };

  const handleCopyLink = async () => {
    const ok = await copyToClipboard(`${shareText}\n${businessUrl}`);
    if (ok) {
      trackShare('copy');
      setCopied(true);
      setCopiedFor('link');
      setTimeout(() => { setCopied(false); setCopiedFor(''); }, 2000);
      showFeedback('clipboard');
    }
  };

  const sizes = { sm: 'w-8 h-8', md: 'w-10 h-10', lg: 'w-12 h-12' };
  const iconSizes = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };

  // Modal is rendered via portal so it always escapes any parent stacking
  // context (cards have transforms / hover effects that previously trapped
  // the modal under floating icons on adjacent cards).
  const modal = !showModal ? null : (
    <div
      className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
      onClick={() => setShowModal(false)}
      data-testid="share-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="share-modal-content"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
            Compartilhar
          </h3>
          <button
            onClick={() => setShowModal(false)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-[var(--text-secondary)] mb-4">
          Compartilhe {business.nome} com sua comunidade
        </p>

        {canNativeShare && (
          <button
            onClick={handleNativeShare}
            className="w-full flex items-center gap-3 p-3 mb-3 rounded-xl bg-[var(--primary)] text-white hover:bg-[var(--primary)]/90 transition-colors"
            data-testid="share-native"
          >
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Compartilhar…</div>
              <div className="text-xs opacity-80">Stories, Status e mais</div>
            </div>
          </button>
        )}

        <div className="space-y-2">
          <button
            onClick={handleWhatsAppShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:bg-gray-50 transition-colors"
            data-testid="share-whatsapp"
          >
            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium">WhatsApp (conversa)</span>
          </button>

          <button
            onClick={handleWhatsAppStatusShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:bg-gray-50 transition-colors"
            data-testid="share-whatsapp-status"
          >
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium flex-1 text-left">
              {copiedFor === 'status' ? 'Texto copiado! Cole no Status' : 'Status do WhatsApp'}
            </span>
            {copiedFor === 'status' && <Check className="w-4 h-4 text-green-600" />}
          </button>

          <button
            onClick={handleInstagramShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:bg-gray-50 transition-colors"
            data-testid="share-instagram"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}>
              <Instagram className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium flex-1 text-left">
              {copiedFor === 'instagram' ? 'Link copiado! Cole no Stories ou bio' : 'Instagram'}
            </span>
            {copiedFor === 'instagram' && <Check className="w-4 h-4 text-green-600" />}
          </button>

          <button
            onClick={handleFacebookShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:bg-gray-50 transition-colors"
            data-testid="share-facebook"
          >
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <Facebook className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium">Facebook</span>
          </button>

          <button
            onClick={handleXShare}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:bg-gray-50 transition-colors"
            data-testid="share-x"
          >
            <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
              <XIcon className="w-4 h-4 text-white" />
            </div>
            <span className="font-medium">X (Twitter)</span>
          </button>

          <button
            onClick={handleCopyLink}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--border)] hover:bg-gray-50 transition-colors"
            data-testid="copy-link"
          >
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <LinkIcon className="w-5 h-5 text-gray-700" />
            </div>
            <span className="font-medium flex-1 text-left">
              {copied ? 'Link copiado!' : 'Copiar link'}
            </span>
            {copied && <Check className="w-4 h-4 text-green-600" />}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowModal(true);
        }}
        className={`${sizes[size]} rounded-full bg-white text-[var(--text-secondary)] hover:text-[var(--primary)] flex items-center justify-center transition-all shadow-md hover:shadow-lg active:scale-95 ${className}`}
        data-testid={`share-button-${business?.id || 'item'}`}
        aria-label="Compartilhar"
      >
        <Share2 className={iconSizes[size]} />
      </button>

      {modal && createPortal(modal, document.body)}

      <CompartilharFeedback
        visivel={feedbackVisivel}
        metodo={feedbackMetodo}
        onFechar={() => { setFeedbackVisivel(false); setFeedbackMetodo(null); }}
      />
    </>
  );
}
